/*
 * Copyright 2023 Adobe. All rights reserved.
 * This file is licensed to you under the Apache License, Version 2.0 (the "License")
 * you may not use this file except in compliance with the License. You may obtain a copy
 * of the License at http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software distributed under
 * the License is distributed on an "AS IS" BASIS, WITHOUT WARRANTIES OR REPRESENTATIONS
 * OF ANY KIND, either express or implied. See the License for the specific language
 * governing permissions and limitations under the License.
 */

import React, { useEffect, useState,  useRef } from 'react';
import {
  Provider,
  defaultTheme,
  Heading,
  Button,
  ButtonGroup,
  TextField,
  Content,
  Form,
  Text,
} from '@adobe/react-spectrum';
import { Item, ListView } from '@react-spectrum/list';
import Spinner from './Spinner';
import { generateBrandTheme, sendAnalyticsData } from '../utils';
import { useParams } from 'react-router-dom';
import { attach } from '@adobe/uix-guest';
import { extensionId } from './Constants';



/**
 *
 */
export default function () {
  const [isLoading, setIsLoading] = useState(true);
  const [translations, setTranslations] = useState([]);
  const [selectedTranslations, setSelectedTranslations] = useState([]);
  const [managementIsDisabled, setManagementIsDisabled] = useState(true);
  const [, setGuestConnection] = useState();
  const [sharedContext, setSharedContext] = useState();
  const [liveSiteURL, setLiveSiteURL] = React.useState("");
  const [urlError, setUrlError] = useState("");

  const guestConnectionRef = useRef(null);
  const { cfPath } = useParams();
  if (!cfPath) {
    console.error('[brand-theme-generator] Content fragment paths parameter is missed');
    return;
  }

  /**
   * Validates if the provided string is a valid URL
   * @param {string} url - The URL string to validate
   * @returns {boolean} - True if valid URL, false otherwise
   */
  const isValidURL = (url) => {
    if (!url || url.trim() === '') {
      return false;
    }
    
    try {
      const urlObj = new URL(url);
      return urlObj.protocol === 'http:' || urlObj.protocol === 'https:';
    } catch (error) {
      return false;
    }
  };

  /**
   * Handles URL input changes and validates the URL
   * @param {string} value - The new URL value
   */
  const handleURLChange = (value) => {
    setLiveSiteURL(value);
    
    if (value.trim() === '') {
      setUrlError('');
      setManagementIsDisabled(true);
    } else if (isValidURL(value)) {
      setUrlError('');
      setManagementIsDisabled(false);
    } else {
      setUrlError('Valid URL expected');
      setManagementIsDisabled(true);
    }
  };

  useEffect(() => {
    let interval;

    const init = async () => {
      const guestConnection = await attach({ id: extensionId });
      guestConnectionRef.current = guestConnection; 
      //setGuestConnection(guestConnection);
      setSharedContext(guestConnection.sharedContext);
      setIsLoading(false);
    };
    init().catch(console.error);

    return () => clearInterval(interval);
  }, []);

  /**
   * Handles the submission of the workflow request
   * Shows a success toast and closes the modal on success
   * Shows an error toast on failure
   */
  const onFetchThemeHandler = async () => {
    setIsLoading(true);
    setManagementIsDisabled(true);
    // console.log('[brand-theme-generator] sharedContext.get(client_id) : ', sharedContext.get('client_id'));
    
    const aemHost = sharedContext.get('aemHost')

    let analyticsData = [
      {key: 'eVar16', value: 'brand-theme'}, 
      {key: 'eVar17', value: liveSiteURL}, 
      {key: 'events', value: 'event11'}
    ];
    
    sendAnalyticsData(analyticsData, aemHost);

    try {
      const response = await generateBrandTheme(cfPath, liveSiteURL, sharedContext.get('auth'), aemHost);
      
      // console.log('[brand-theme-generator] Full response from generateBrandTheme:', response);
      // The response structure is: {fragmentPath: '/content/dam/wknd-universal/en/fragments/brand-themes/custom-theme', message: 'Successfully updated theme values in AEM', status: 'success'}
      if (response && response.status === 'success'){

        analyticsData = [
          {key: 'eVar16', value: 'brand-theme'}, 
          {key: 'eVar17', value: liveSiteURL}, 
          {key: 'events', value: 'event9'}
        ];
        sendAnalyticsData(analyticsData, aemHost);


        // const guestConnection = await attach({ id: extensionId });
        const guestConnection = guestConnectionRef.current;
        // Refresh the content fragment to show updated values
        guestConnection.host.modal.close();
        await guestConnection.host.contentFragment.refresh();       
      } else {

        analyticsData = [
          {key: 'eVar16', value: 'brand-theme'}, 
          {key: 'eVar17', value: liveSiteURL}, 
          {key: 'events', value: 'event8'}
        ];
        sendAnalyticsData(analyticsData, aemHost);

        console.error('[brand-theme-generator] Brand theme generation failed:', response);
        setManagementIsDisabled(false);
      }
    } catch (error) {
      console.error('[brand-theme-generator] Error generating brand theme:', error);
      setManagementIsDisabled(false);
    } finally {
      setIsLoading(false);
    }
  }

  return (
      <Provider theme={defaultTheme} colorScheme={'light'}>
        <Content width="97%">
          {isLoading ? (
            <Spinner />
          ) : (
            <Form necessityIndicator="label">
                <TextField
                  label="Live Site URL e.g. https://adobe.com"
                  value={liveSiteURL}
                  onChange={handleURLChange}
                  margin="size-175"
                  errorMessage={urlError}
                  isInvalid={!!urlError}
                  isRequired
                />
                <Button
                  variant="cta"
                  type="button"
                  isDisabled={managementIsDisabled}
                  onPress={onFetchThemeHandler}
                  marginTop="size-175"
                >
                  Fetch Theme
                </Button>
              </Form>
            )}
        </Content>
      </Provider>
  )
}
